import {useMemo} from 'react';

import {useQuery} from '@tanstack/react-query';
import {useDispatch, useSelector} from 'react-redux';
import {forEachObj, mapKeys} from 'remeda';

import {ENDPOINTS} from 'constants/endpoints';

import {apiConfig} from 'libs/api/api';
import {deleteParam, getUrlParams} from 'libs/url/url';
import {MessageType} from 'types/cartType';

import {getCartQuoteSaga} from '../../store/actions';
import {setPlaceOfInteraction} from '../../store/reducers/placeOfInteraction/placeOfInteraction';
import {getCart} from '../../store/selectors';

const CART_DEEP_LINK_KEY = 'CART_DEEP_LINK_KEY';
const SKU_PARAM = 'addsku';
const PROMO_PARAM = 'promo';

const deleteParams = (sku: string) => {
    if (!sku) return;
    const params: Record<string, string[]> = getUrlParams();
    forEachObj.indexed(params, (_, key) => {
        if ([PROMO_PARAM, SKU_PARAM].includes(key.toLowerCase())) deleteParam(key, true);
    });
};

const getQueryParams = () => {
    const params = mapKeys(getUrlParams(), key => String(key).toLowerCase());
    const sku = params[SKU_PARAM];
    const promo = params[PROMO_PARAM];

    return {promo: promo?.[0], sku: sku?.[0]};
};

/**
 * When in cart page there is sku query param this hook add it in quote and refresh it
 * if there is promo query param, we apply it as coupon
 * @returns {{isLoaded: boolean, errors: MessageType[]}} return isLoaded and errors
 */
export const useCartDeepLink = () => {
    const dispatch = useDispatch();
    const {quoteItems, status: quoteItemsStatus} = useSelector(getCart);
    const isSkuExist = (sku: string) =>
        Boolean(quoteItems.find(item => item.product_sku.toLowerCase() === sku.toLowerCase()));

    const {error, isError, isSuccess} = useQuery({
        enabled: quoteItemsStatus === 'fulfilled',
        queryFn: async () => {
            try {
                const {promo, sku} = getQueryParams();
                deleteParams(sku);

                if (sku && !isSkuExist(sku)) {
                    const data = {
                        coupon: promo || null,
                        productSku: sku,
                    };

                    await apiConfig.instanceGusaBackend({
                        data,
                        method: 'POST',
                        url: ENDPOINTS.ADD_TO_CART_V2,
                    });

                    setPlaceOfInteraction('Google shopping');
                    await dispatch(getCartQuoteSaga());
                }

                return null;
            } catch (e) {
                throw (
                    (e?.response?.data?.messages?.error as MessageType[])?.map(({message}) => ({
                        message,
                        type: 'error',
                    })) || [
                        {
                            message: 'Something went wrong, please try again later',
                            type: 'error',
                        },
                    ]
                );
            }
        },
        queryKey: [CART_DEEP_LINK_KEY],
        refetchOnWindowFocus: false,
        retry: false,
    });

    const errors = useMemo(() => (error || []) as MessageType[], [error]);

    return {
        errors,
        isLoaded: isSuccess || isError,
    };
};
